/* eslint-disable max-lines-per-function */
import React from "react";
import { Router, LocationContext, navigate } from "@reach/router";
import { Viewer } from "components/auth/types";
import {
  Feature,
  QueryInterestProfile_interest as InterestType,
  QueryInterestProfile_interest_interestList_property as PropertyType,
  QueryInterestProfile_interest_user as UserType,
} from "types/gql-types";
import { isPropertyFeatureEnabled } from "pages/dashboard/interests";
import Documents from "../../interests-documents/components/index";
import InterestUpdatePage from "../../interests-update/interest-update-page";
import Star from "../../interests/interests-table/star";
import Activity from "./activity-page";
import Applications from "./applications-page";
import Actions from "./actions";
import Navigation from "./navigation";
import Metadata from "./metadata";
import styles from "./index.module.css";

export const links = [
  { name: "Activity", to: "activity" },
  { name: "Applicant Info", to: "update" },
  {
    name: "Documents",
    to: "documents",
    feature: Feature.DOCUMENT_UPLOAD,
  },
];

interface Props {
  viewer: Viewer;
  location: LocationContext;
  onStar: (bool: boolean) => void;
  setBatchActionsModalFocusedAction: React.Dispatch<React.SetStateAction<undefined>>;
  currentInterestID: string;
  householdMember0FirstName: string | null;
  householdMember0LastName: string | null;
  interestListID: string;
  property: PropertyType;
  householdCount: string;
  householdGrossAnnualIncome: number | null;
  status: string;
  onSubmit: () => Promise<void>;
  sendFullAppToInterestsEnabled: boolean;
  interest: InterestType;
  user: UserType | null;
}

const formatName = ({ householdMember0FirstName, householdMember0LastName }: Props) =>
  [householdMember0FirstName, householdMember0LastName].filter(Boolean).join(" ");

export const ApplicantProfileView: React.FC<Props> = (props) => {
  const { interest, currentInterestID, interestListID, onSubmit, user,
    property: { features } } = props;
  if (typeof currentInterestID === "undefined") return null;

  const updateProps = {
    interestID: interest.id,
    interestListID,
    onSubmit,
    onCancel: () => navigate(".."),
  };
  const documentFeatureEnabled = isPropertyFeatureEnabled(features, Feature.DOCUMENT_UPLOAD);

  const currentInterestName = formatName(props);

  return (
    <div className={styles.profile}>
      <div className={`${styles.header}`}>

        <div className={styles.labels}>
          <Star
            currentInterestID={interest.id}
            hasStar={!!interest.hasEligibility}
            onStar={props.onStar}
          />
          <span className={styles.label}>{currentInterestName}</span>
        </div>

        <Metadata
          householdCount={props.householdCount}
          householdGrossAnnualIncome={ props.householdGrossAnnualIncome }
          position={interest.positionNumber}
          loginDetails={user?.loginEmail ?? user?.loginPhone}
          interestListName={interest.interestList.name}
        />

        <div className={styles.actions}>
          <Navigation interestId={interest.id} />

          <Actions
            currentInterestName={currentInterestName}
            property={props.property}
            currentInterestID={interest.id}
            interestListID={props.interestListID}
            status={props.status}
            setBatchActionsModalFocusedAction={props.setBatchActionsModalFocusedAction}
            sendFullAppToInterestsEnabled={ props.sendFullAppToInterestsEnabled }
          />
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.content}>
          <Router primary={false}>
            <Activity path="/activity" interestID={currentInterestID} />
            <Activity path="/" interestID={currentInterestID} />
            <InterestUpdatePage path="/update" {...updateProps} />
            <Documents path="/documents" interestID={currentInterestID} enabled={documentFeatureEnabled} />
            <Applications
              path="/applications"
              interestId={currentInterestID}
              viewer={props.viewer}
            />
          </Router>
        </div>
      </div>
    </div>
  );
};
